<template>
    <div
        :style="{ zIndex : (headerBarZIndex - 1) }"
        class="MobileAppFareMediaContextBar"
    >
        <div
            v-if="!isAuthenticated"
            class="MobileAppFareMediaContextBar-header"
        >
            <p class="MobileAppFareMediaContextBar-headerText">
                {{ $t('mobile_app_context_bar:login_advantages') }}
            </p>
            <v-btn
                color="primary"
                size="large"
                variant="flat"
                @click.prevent="logIn"
            >
                {{ $t('button:login') }}
            </v-btn>
        </div>
        <div
            :class="[(getHasSelectedFareMedia ? 'MobileAppFareMediaContextBar-content--withSelectedFareMedia' : 'MobileAppFareMediaContextBar-content--withoutSelectedFareMedia')]"
            class="MobileAppFareMediaContextBar-content"
        >
            <button
                class="MobileAppFareMediaContextBar-switchContextButton"
                @click.prevent="showFareMediaChoiceModal"
            >
                <span
                    v-if="selectedFareMedia"
                    :class="'Icon' + selectedFareMedia.getIcon()"
                    class="MobileAppFareMediaContextBar-contextIcon"
                />
                <div
                    :class="[ selectedFareMedia ? 'MobileAppFareMediaContextBar-contextText--fareMedia' : 'MobileAppFareMediaContextBar-contextText--wholeCatalog' ]"
                    class="MobileAppFareMediaContextBar-contextText"
                >
                    <div class="MobileAppFareMediaContextBar-contextTitle">
                        {{ getTitle }}
                    </div>
                    <div
                        v-if="getSubtitle !== ''"
                        class="MobileAppFareMediaContextBar-contextSubtitle"
                    >
                        <span class="MobileAppFareMediaContextBar-contextSubtitleLabel">{{ getSubtitle }}</span>
                        <icomoon-icon
                            name="Change--outlined"
                            class="MobileAppFareMediaContextBar-switchContextButtonIcon"
                        />
                    </div>
                </div>
            </button>
            <v-btn
                v-sticky
                :aria-label="$t('button:filter')"
                :title="$t('button:filter')"
                class="MobileAppFareMediaContextBar-showFiltersButton"
                icon
                @click="showCatalogFilters"
            >
                <icomoon-icon
                    :layersCount="0"
                    :name="'Filter'"
                    class="Home-filtersButtonIcon"
                />
                <div
                    v-if="appliedFiltersCount"
                    class="MobileAppFareMediaContextBar-appliedFiltersCount"
                >
                    {{ appliedFiltersCount }}
                </div>
            </v-btn>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import IcomoonIcon from '@/StoreWeb/components/common/IcomoonIcon.vue'
import { useStore } from 'vuex'
import { useContextBar } from '@/StoreWeb/js/composables/useContextBar'
import { useLogin } from '@/StoreWeb/js/composables/login-utils'
import addCartFlowMixins from '@/StoreWeb/js/mixins/add-cart-flow-utils'

const store = useStore()
const { isAuthenticated } = useLogin()
const { triggerLogin } = addCartFlowMixins()
const { headerBarZIndex, selectedFareMedia, getHasSelectedFareMedia, getTitle, getSubtitle } = useContextBar()

const emit = defineEmits(['showCatalogFilters', 'switchFareMediaContext'])

const appliedFilters = computed(() => store.state.catalogModule.appliedFilters)
const appliedFiltersCount = computed(() => appliedFilters.value?.filter(filter => filter.value).length || 0)

function logIn () {
    triggerLogin()
}

function showCatalogFilters () {
    emit('showCatalogFilters')
}

function showFareMediaChoiceModal () {
    emit('switchFareMediaContext', this)
}
</script>

<style lang="scss" scoped>
@import 'globalScss';

.MobileAppFareMediaContextBar {
    &-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: $s2;
        width: 100%;
        padding: $s2 $s6;
        background: $color_lightgray5;
    }

    &-headerText {
        margin: 0;
        font-size: $font-xxsmall;
    }

    &-content {
        display: flex;
        justify-content: space-between;
        min-height: calc(48px + 2 * $s2);
        padding: $s2 $s6;
    }

    &-switchContextButton {
        display: flex;
        align-items: center;
        gap: $s1;
    }

    &-contextIcon {
        font-size: 32px;
        color: $color-brandPrimary;
        transform: rotate(-6deg);
    }

    &-contextText {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }

    &-contextTitle,
    &-contextSubtitle {
        display: inline;
        margin: 0;
        font-size: $font-smedium;
    }

    &-contextTitle {
        font-family: $fontFamily-title;
        font-weight: $fontWeight-titleBold;
    }

    &-contextSubtitle {
        display: flex;
        align-items: center;
        font-size: $font-xsmall;
        color: $color-lighterText;

        &Label {
            font-weight: $fontWeight-defaultMedium;
        }
    }

    &-contextText--wholeCatalog {
        .MobileAppFareMediaContextBar-contextTitle {
            line-height: 1.2;
        }
    }

    &-contextText--fareMedia {
        .MobileAppFareMediaContextBar-contextTitle {
            font-size: $font-xsmall;
        }
    }

    &-switchContextButtonIcon {
        font-size: 24px;
    }

    &-showFiltersButton {
        position: relative;
        z-index: 2;
        border-radius: 50% !important;
        box-shadow: 0 0 8px 0 rgba(0, 0, 0, .3);
        font-size: 24px;
    }

    &-appliedFiltersCount {
        position: absolute;
        top: $ns1;
        right: $ns2;
        min-width: 22px;
        padding: $s1;
        border-radius: 50%;
        background: $color_info;
        font-size: $font-xxsmall;
        color: $color-lightest;
    }
}
</style>
